import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import DashboardView from './DashboardView'

import { State } from 'admin/redux/ducks'
import {
  fetchActivePurchasersCount,
  fetchTransactionStats,
  DashboardState,
  InvoiceCountState,
} from 'admin/redux/ducks/dashboard'
import {
  fetchInvoicesWithParams,
  InvoicesState,
} from 'admin/redux/ducks/invoices'
import { LoginState } from 'admin/redux/ducks/login'
import {
  OrgAccountState,
  fetchOrganization,
} from 'admin/redux/ducks/orgAccount'
import { InvoiceStatus, InvoiceSpecificationSummary } from 'admin/types'
import { InvoicesParamsType } from 'admin/types/api'
import { withScrollToTop } from 'components/common/withScrollToTop'
import { hideTooltipAction } from 'redux/ducks/tooltip'

const invoicesParams: InvoicesParamsType = {
  filters: {
    status: [
      InvoiceStatus.COLLECTION,
      InvoiceStatus.INVOICED,
      InvoiceStatus.REMINDER,
    ],
  },
}

export const invoicesToInvoiceCountState = (
  invoicesState: InvoicesState
): InvoiceCountState => {
  if (!invoicesState.data) {
    return {
      error: invoicesState.error,
      isLoading: invoicesState.isLoading,
    }
  }
  const invoicesCollection = invoicesState.data.invoices.filter(
    (invoice: InvoiceSpecificationSummary) =>
      invoice.status === InvoiceStatus.COLLECTION
  )
  const invoicesInvoiced = invoicesState.data.invoices.filter(
    (invoice: InvoiceSpecificationSummary) =>
      invoice.status === InvoiceStatus.INVOICED
  )
  const invoicesReminder = invoicesState.data.invoices.filter(
    (invoice: InvoiceSpecificationSummary) =>
      invoice.status === InvoiceStatus.REMINDER
  )
  return {
    data: {
      invoiceCount: {
        collection: invoicesCollection.length,
        invoiced: invoicesInvoiced.length,
        reminder: invoicesReminder.length,
      },
    },
    error: invoicesState.error,
    isLoading: invoicesState.isLoading,
  }
}

const Dashboard = (props: DashboardProps) => {
  const navigate = useNavigate()
  const prevProps = useRef(props)

  const fetchData = (orgAccountId, orgKimId) => {
    // Only one request at first to catch and resolve login errors before moving on to other requests
    props.fetchActivePurchasersCount(orgAccountId).then(() => {
      props.fetchInvoices(orgAccountId, invoicesParams)
      props.fetchTransactionStats(orgAccountId)
      props.fetchOrganization(orgKimId)
    })
  }

  const navigateTo = (location) => {
    const { hideTooltip } = props
    hideTooltip()
    navigate(location)
  }

  useEffect(() => {
    if (props.login) {
      fetchData(props.login.data.orgAccountId, props.login.data.orgKimId)
    }
  }, [])

  useEffect(() => {
    if (
      props.login &&
      prevProps.current.login &&
      props.login.data.orgAccountId !==
        prevProps.current.login.data.orgAccountId
    ) {
      fetchData(props.login.data.orgAccountId, props.login.data.orgKimId)
    }

    prevProps.current = props
  }, [props.login])

  const { login, dashboard, invoices, organization } = props
  const dashboardProps = {
    login,
    dashboard,
    navigateTo,
    invoiceCount: invoicesToInvoiceCountState(invoices),
    organization,
  }
  return <DashboardView {...dashboardProps} />
}

type DashboardProps = {
  dashboard: DashboardState
  login: LoginState
  invoices: InvoicesState
  organization: OrgAccountState
  fetchOrganization: (orgKimId: string) => Promise<any>
  fetchActivePurchasersCount: (orgAccountId: string) => Promise<any>
  fetchInvoices: (
    orgAccountId: string,
    params: InvoicesParamsType
  ) => Promise<any>
  fetchTransactionStats: (orgAccountId: string) => Promise<any>
  hideTooltip: () => any
}

const mapStateToProps = (state: State) => {
  return {
    dashboard: state.dashboard,
    login: state.login as LoginState,
    invoices: state.invoices,
    organization: state.organization,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchOrganization: (orgKimId) => dispatch(fetchOrganization(orgKimId)),
    fetchActivePurchasersCount: (orgAccountId) =>
      dispatch(fetchActivePurchasersCount(orgAccountId)),
    fetchInvoices: (orgAccountId, status) =>
      dispatch(fetchInvoicesWithParams(orgAccountId, status)),
    fetchTransactionStats: (orgAccountId) =>
      dispatch(fetchTransactionStats(orgAccountId)),
    hideTooltip: () => dispatch(hideTooltipAction()),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withScrollToTop(Dashboard, { onMount: true, onUpdate: true }))
